import { Injectable } from '@angular/core';
import { SystemPermission, SystemPermissions } from '@app/interfaces';
import { ApiRequestService } from '@app/core/http/api-request.service';
// import { UserDetailsService } from '@app/shared/services/user-details/user-details.service';
import { Observable, of, Subject } from 'rxjs';
import { catchError, map, take } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class PermissionsService {
  public refresh: Subject<any> = new Subject();
  public permissionsUpdated: Subject<any> = new Subject();
  private permissions: string[];
  private permissionsByModule: SystemPermissions;

  constructor(private apiRequestService: ApiRequestService) {}

  /**
   * helper function for easier mapping in template for better performance on the dom
   * and not using getPermissions directly in templating
   * @param module: module permissions key
   */
  public getPermissionsHelper(module?: string): string[] {
    return this.permissions;
  }
  public getPermissionsByModuleHelper(module?: string): SystemPermission | boolean {
    return this.permissionsByModule[module];
  }

  /**
   * helper function to return permissions as boolean only
   * @param module: module permission key
   * @param key: sub module permissions key
   */
  public booleanPermissionsChecker(key?: string): boolean {
    if (key) {
      return this.getPermissions(key);
    }
    return false;
  }

  /**
   * helper function to return permissions as boolean only
   * @param module: module permission key
   * @param key: sub module permissions key
   */
  public getAllPermissions(): string[] {
    return this.permissions;
  }

  /**
   * for any section in system to check for specific permission with module and key both optional,
   * or to return the full permissions of system
   * @param module: module permission key
   * @param key: sub module permissions key
   */
  public getPermissions(key?: string): boolean {
    if (key) {
      const hasPermission = this.permissions?.includes(key);
      return hasPermission;
    }

    return false;
  }

  /**
   * Permissions Request Api function checks if there isn't loaded permissions then request the api and then
   * call a callback function provided with the request as optional
   * @param options : {
   *        getPermissions: optional callback function to be executed on successful api response or if permissions
   *        already exist on service, should be the same getPermissions function with module and key
   *        if they are provided. unless another logic is needed,
   *        refresh: if needed to force refresh permissions with an API request
   * }
   */
  public permissionsRequest(options?: {
    getPermissions?: (key?: string) => boolean | SystemPermissions;
    refresh?: boolean;
  }): Observable<boolean> {
    if (
      this.permissions === undefined ||
      this.permissions === null ||
      !this.permissions.length ||
      (options && options.refresh)
    ) {
      return this.apiRequestService.request('GET', 'users/permissions').pipe(
        map(res => {
          const refactoredPermissions = this.refactorPermissions(res.data);
          this.setPermissions(refactoredPermissions);
          this.setPermissionsByModule(this.refactorPermissionsByModule(res.data));
          this.permissionsUpdated.next(null);
          const result = options?.getPermissions ? !!options.getPermissions() : true;
          return result;
        }),
        catchError(err => {
          console.error('PermissionsService - Error loading permissions:', err);
          this.setPermissions([]);
          return of(false).pipe(take(1));
        })
      );
    } else {
      const result = options && options.getPermissions ? !!options.getPermissions() : false;
      return of(result).pipe(take(1));
    }
  }

  /**
   * public method to update Private Permissions object with in the service
   * @param permissions : permissions data to be set,
   * if not provided then the function clears the permissions and set it to undefined
   */
  public setPermissions(permissions?: string[]) {
    this.permissions = permissions;
  }
  /**
   * public method to update Private Permissions object with in the service
   * @param permissions : permissions data to be set,
   * if not provided then the function clears the permissions and set it to undefined
   */
  public setPermissionsByModule(permissions?: SystemPermissions) {
    this.permissionsByModule = permissions;
  }

  /**
   * refactor logic for permissions response
   * @param data : response data to parse
   */
  refactorPermissions(data: any): string[] {
    // let permissions: SystemPermissions | boolean = false;
    const permissions: string[] = [];
    if (data) {
      // here todo the logic
      Object.keys(data).forEach((key: string) => {
        permissions.push(...data[key].filter((x: any) => x.is_selected).map((x: any) => x.name));
      });
      // console.log(permissions);
    }
    // console.log(permissions);
    return permissions;
  }
  /**
   * refactor logic for permissions response
   * @param data : response data to parse
   */
  refactorPermissionsByModule(data: any): SystemPermissions {
    let permissions: SystemPermissions = {};
    // const permissions: string[] = [];
    if (Object.keys(data).length) {
      // here todo the logic
      for (const key in data) {
        permissions[key] = this.convertArrayToObject(data[key]);
      }
    }
    // console.log(permissions);
    return permissions;
  }

  convertArrayToObject(array: any[]) {
    let obj = {};
    array.forEach((el: any) => {
      obj[el['name'].split('-')[0]] = el['is_selected'];
    });
    return obj;
  }
  /**
   * refactor logic for user data response
   * @param data : response data to parse
   */
  refactorUserData(data: any) {
    if (data) {
      const userDetails = { ...data };
      delete userDetails.permissions;
      return userDetails;
    }
  }
}
